<template>
  <div class="setting-dropdown-question">
    <InfoContainer  state="error">
    <QuestionTitle v-if="question" :question="question" :disabled="disabled || salesChange" :required="required" />
    <SingleSelector
      ref="selectRef"
      :model-value="modelValue"
      :placeholder="placeholder"
      :disabled="disabled || salesChange"
      :filterable="filterable"
      :options="options"
      :remote_search="remote"
      :remote_search_pagination="hasMore"
      :remote_search_has_more="hasMore"
      :remote_search_load_more_loading="loading"
      :loading="loading"
      :customizedOptionStyle="!!$slots.option"
      @update:model-value="handleUpdateModelValue"
      @change="handleChange"
      @remote-search="handleRemoteSearch"
      @remote-search-load-more="handleLoadMore"
    >
      <template v-if="$slots.option" #option="{ option, is_selected, disabled: optDisabled }">
        <slot name="option" :item="option" :is_selected="is_selected" :disabled="optDisabled">{{ option.label }}</slot>
      </template>
    </SingleSelector>
    <div v-if="description" class="setting-dropdown-notes mt-2">{{ description }}</div>
    <div v-if="selectedDescription" class="setting-dropdown-notes mt-2">{{ selectedDescription }}</div>
    </InfoContainer>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, ref, computed, watchEffect } from 'vue';
import QuestionTitle from './QuestionTitle.vue';

const props = defineProps({
  options: {
    type: Array,
    default() {
      return [{ label: '', value: '' }];
    },
  },
  question: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: 'Select',
  },
  modelValue: {
    type: [String, Number],
    default: '',
  },
  required: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
  salesChange: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  description: {
    type: String,
  },

  showOptional: {
    defalut: false,
  },
  prefix: {
    type: String,
    default: null,
  },
  filterable: {
    type: Boolean,
    default: false,
  },
  remote: {
    type: Boolean,
    default: false,
  },
  remoteMethod: {
    type: Function,
    default: null,
  },
  selectWidth: {
    type: String,
    default: '50%',
  },
  loading: {
    type: Boolean,
    default: false,
  },
  hasMore: {
    type: Boolean,
    default: false,
  },
  // Kept for backward compat with existing call-sites; SingleSelector renders
  // its own dropdown so this prop is effectively a no-op now.
  popperClass: {
    type: String,
    default: 'setting-dropdown-popper',
  },
});

const emit = defineEmits(['update:modelValue', 'validateInput', 'change', 'load-more']);

const selectRef = ref(null);

const handleChange = (value) => {
  emit('change', value);
};

const handleUpdateModelValue = (value) => {
  emit('update:modelValue', value);
};

// SingleSelector emits `remote-search` instead of taking a `remoteMethod`
// prop. Forward the keyword to the legacy callback so callers don't have to
// change their wiring.
const handleRemoteSearch = (query) => {
  if (typeof props.remoteMethod === 'function') props.remoteMethod(query);
};

// SingleSelector handles popper-scroll detection internally and emits this
// event when the user scrolls near the bottom (gated by `remote_search`,
// `remote_search_pagination`, and `remote_search_has_more`). Re-emit as
// `load-more` to preserve the original parent contract.
const handleLoadMore = () => {
  emit('load-more');
};

const selectedDescription = computed(() => {
  let target = props.modelValue;
  let options = [...(props?.options || [])];
  let selected = options.filter((element) => element?.value === target);
  return selected.length && selected[0].description ? selected[0].description : '';
});

const validateInput = (val) => {
  //if show error before user change, set initial value to '', else set to null
  if (props.disabled) {
    emit('validateInput', true);
  } else {
    let validated = props.required ? val != '' : true;
    emit('validateInput', validated);
  }
};

watchEffect(() => {
  validateInput(props.modelValue);
});
</script>

<style scoped>
.setting-dropdown-notes {
  font-size: 15px;
  line-height: 18px;
  color: rgba(151, 151, 151, 1);
}
</style>


